import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import NothingHere from '../../custom-components/NothingHere';
import { useNavigatorStore } from '../../zustand/useNavigatorStore';
import { PeopleResults } from '../PeopleResults';
import {
  hasNavigatorQuery,
  navigatorPeopleConditions,
  navigatorPeopleUnsupported,
  parseNavigatorQuery,
} from './navigator-query';
import './NavigatorList.scss';

/**
 * The Navigator's People view: the people the panel's query (?q=) yields, as the People
 * directory lists them, or by account (?group=account) as the Buying Groups page does. Nothing
 * is searched until the panel's Search puts a query in the URL, since every page returned counts
 * against the team's revealed-contacts quota.
 */
export function NavigatorPeople() {
  const location = useLocation();
  const setIsLoadingList = useNavigatorStore((state) => state.setIsLoadingList);
  // The panel's Search button spins while the list loads; not once this view is gone.
  useEffect(() => () => setIsLoadingList(false), [setIsLoadingList]);
  const params = new URLSearchParams(location.search);
  const q = params.get('q') ?? '';
  const groupBy = params.get('group') === 'account' ? 'account' : undefined;
  const query = useMemo(() => parseNavigatorQuery(location.search), [q]);
  const conditions = useMemo(() => navigatorPeopleConditions(query), [query]);
  const unsupported = useMemo(() => navigatorPeopleUnsupported(query), [query]);

  if (!hasNavigatorQuery(location.search)) {
    return (
      <div className="navigator-list">
        <NothingHere text="Set filters in the panel and search to list people." />
      </div>
    );
  }

  return (
    <div className="navigator-list">
      {unsupported.length > 0 && (
        <div className="navigator-list__note">
          Not applied to people yet: {unsupported.join(', ')}.
        </div>
      )}
      <PeopleResults
        groupBy={groupBy}
        conditions={conditions}
        searchKey={q}
        onLoadingChange={setIsLoadingList}
        stickyHeader
      />
    </div>
  );
}
